import { getErrorCode } from '@repo/ui/utils'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { FileLock, Trash2, TriangleAlert } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { useTranslation } from '@/lib/i18n'
import { PreviewActions } from '@/modules/file-manager/components/file-preview'
import { FilePreviewBody } from '@/modules/file-manager/components/file-preview-body'
import { nodeNameTitle } from '@/modules/file-manager/components/node-name'
import {
	PREVIEW_TITLE,
	PreviewBar,
	PreviewStage
} from '@/modules/file-manager/components/preview-frame'
import { PreviewLoading } from '@/modules/file-manager/components/preview-states'
import { UnreachableState } from '@/modules/file-manager/components/unreachable-state'
import { nodeDetailQueryOptions } from '@/modules/file-manager/query/query-options'

export const Route = createFileRoute('/(private)/view/$nodeId/')({
	component: ViewPage
})

/**
 * One file on a page of its own — what «Apri in un'altra scheda» opens, from a
 * row's menu or from the preview dialog's bar.
 *
 * **The whole window, with no sidebar.** It is the file and nothing else, as
 * the dialog is. The route sits beside `(shell)`, the group that draws the
 * sidebar, so the window is the page's own to fill.
 *
 * **The dialog's bar and stage, without the dialog.** Nothing closes here — it
 * is a tab, and the browser closes tabs — so the bar is the file's name and
 * the dialog's own actions (`PreviewActions`), with two left out. «Open in a
 * new tab» is where this already is. Moving needs the role on the folder
 * around the file, which only a listing reads; the details panel offers it.
 *
 * **Deleting ends the page on what happened**, rather than on the refusal the
 * next read of the node would draw: somebody who just moved a file to the bin
 * should be told that, not that the file may never have been theirs to see.
 *
 * **An address that can be reloaded.** The node is read by its id with the
 * same query the details panel uses, and the file itself is authorized afresh
 * on every mount, so a signed link that expired minutes ago is never what the
 * page depends on. The bytes still travel from the storage to the tab and
 * never through a server of ours.
 *
 * A folder is not a thing this page shows, and neither is a node the caller
 * cannot see: both get the same sentence, because telling them apart would
 * confirm to somebody without access that the id exists.
 */
function ViewPage() {
	const { nodeId } = Route.useParams()
	const { t } = useTranslation()
	const detail = useQuery(nodeDetailQueryOptions(nodeId))
	const [trashed, setTrashed] = useState(false)

	if (trashed) {
		return (
			<ViewRefusal>
				<UnreachableState
					description={t(
						'client:files.unreachable.trashed.description'
					)}
					icon={<Trash2 />}
					title={t('client:files.unreachable.trashed.title')}
				/>
			</ViewRefusal>
		)
	}

	if (detail.isPending) {
		return (
			<ViewShell>
				<PreviewLoading />
			</ViewShell>
		)
	}

	// `not_found` is every node the caller cannot see, whatever the reason; a
	// folder lands here too. Anything else is the read itself failing, which a
	// retry can answer and an access request cannot.
	if (
		(detail.isError && getErrorCode(detail.error) === 'not_found') ||
		(detail.isSuccess && detail.data.data.node.kind !== 'file')
	) {
		return (
			<ViewRefusal>
				<UnreachableState
					description={t('client:files.unreachable.file.description')}
					icon={<FileLock />}
					title={t('client:files.unreachable.file.title')}
				/>
			</ViewRefusal>
		)
	}

	if (detail.isError) {
		return (
			<ViewRefusal>
				<UnreachableState
					description={t(
						'client:files.unreachable.failed.description'
					)}
					icon={<TriangleAlert />}
					onRetry={() => detail.refetch()}
					title={t('client:files.unreachable.failed.title')}
				/>
			</ViewRefusal>
		)
	}

	const item = detail.data.data

	return (
		<div className='flex h-dvh flex-col'>
			<PreviewBar
				actions={
					<PreviewActions
						item={item}
						onTrashed={() => setTrashed(true)}
					/>
				}
				name={item.node.name}
				title={
					<h1
						className={PREVIEW_TITLE}
						title={nodeNameTitle(item.node.name)}
					>
						{item.node.name}
					</h1>
				}
			/>
			<PreviewStage>
				<FilePreviewBody item={item} key={item.node.id} />
			</PreviewStage>
		</div>
	)
}

/**
 * The plain page rather than the stage: the stage's grey is a backdrop for a
 * file, and with no file it only makes the window look broken.
 */
function ViewRefusal({ children }: { children: ReactNode }) {
	return <main className='flex h-dvh'>{children}</main>
}

/** The stage on its own, for the moments there is no file to name yet. */
function ViewShell({ children }: { children: ReactNode }) {
	return (
		<div className='flex h-dvh flex-col'>
			<PreviewStage>{children}</PreviewStage>
		</div>
	)
}
